import {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  FC,
} from "react";
import { tokenStorage } from "./storage";
import type { AuthContextValue, PendingAction } from "../Components/interface";

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(
    null,
  );

  const openLoginModal = useCallback((action: PendingAction) => {
    setPendingAction(() => action);
    setIsModalOpen(true);
  }, []);

  const closeLoginModal = useCallback(() => {
    setIsModalOpen(false);
    setPendingAction(null);
  }, []);

  const onLoginSuccess = useCallback(
    (token: string) => {
      tokenStorage.set(token);
      setIsModalOpen(false);
      if (pendingAction) {
        pendingAction();
        setPendingAction(null);
      }
    },
    [pendingAction],
  );

  return (
    <AuthContext.Provider
      value={{ isModalOpen, openLoginModal, closeLoginModal, onLoginSuccess }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuthContext must be used within <AuthProvider>");
  }
  return ctx;
}
